/**
 * /admin/tickets — lista de tickets de soporte para administradores.
 * /admin/tickets/:id — vista de conversación con form de respuesta.
 *
 * El backend manda email al usuario cada vez que un admin responde, así que
 * desde aquí simplemente escribimos como en cualquier chat y el correo se
 * dispara solo en `supportService.addMessage`.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LifeBuoy,
  Send,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type AdminTicketRow = {
  id: string;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  createdAt: string;
  updatedAt: string;
  user: { email: string; name: string | null };
};

type TicketMessage = {
  id: string;
  ticketId: string;
  authorRole: 'user' | 'admin';
  body: string;
  createdAt: string;
};

type AdminTicketDetail = AdminTicketRow & {
  messages: TicketMessage[];
  user: { id: string; email: string; name: string | null };
};

/* ── Index ───────────────────────────────────────────────── */

export function AdminTickets() {
  const [filter, setFilter] = useState<'all' | 'open' | 'answered' | 'closed'>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tickets'],
    queryFn: () => api<{ tickets: AdminTicketRow[] }>('/api/admin/support/tickets'),
    refetchInterval: 30_000,
  });

  const filtered = (data?.tickets ?? []).filter((t) => filter === 'all' || t.status === filter);
  const openCount = (data?.tickets ?? []).filter((t) => t.status === 'open').length;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <LifeBuoy size={20} />
        </span>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Tickets de soporte</h1>
          <p className="text-sm text-slate-500">
            {openCount > 0 ? `${openCount} ticket${openCount === 1 ? '' : 's'} por responder` : 'No hay tickets pendientes'}
          </p>
        </div>
      </header>

      {/* Filtros */}
      <div className="flex gap-2">
        {(['all', 'open', 'answered', 'closed'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-lg border transition',
              filter === f
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {f === 'all' ? 'Todos' : f === 'open' ? 'Abiertos' : f === 'answered' ? 'Respondidos' : 'Cerrados'}
          </button>
        ))}
      </div>

      {/* Listado */}
      {isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-400 text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          No hay tickets en esta vista.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {filtered.map((t, idx) => (
            <Link
              key={t.id}
              to={`/admin/tickets/${t.id}`}
              className={cn(
                'flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition',
                idx > 0 && 'border-t border-slate-100',
              )}
            >
              <StatusPill status={t.status} />
              <span className="flex-1 min-w-0">
                <span className="block font-medium text-slate-900 truncate">{t.subject}</span>
                <span className="block text-xs text-slate-500">
                  {t.user.name || t.user.email} · Act. {new Date(t.updatedAt).toLocaleDateString('es-ES')}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Detail ──────────────────────────────────────────────── */

export function AdminTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const [reply, setReply] = useState('');
  const qc = useQueryClient();

  const ticketQ = useQuery({
    queryKey: ['admin', 'ticket', id],
    queryFn: () => api<{ ticket: AdminTicketDetail }>(`/api/admin/support/tickets/${id}`),
    enabled: !!id,
  });

  const replyMut = useMutation({
    mutationFn: () =>
      api<{ message: TicketMessage }>(`/api/admin/support/tickets/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: reply }),
      }),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: ['admin', 'ticket', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'tickets'] });
      qc.invalidateQueries({ queryKey: ['admin', 'support', 'open-count'] });
    },
  });

  const closeMut = useMutation({
    mutationFn: () => api(`/api/admin/support/tickets/${id}/close`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ticket', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'tickets'] });
      qc.invalidateQueries({ queryKey: ['admin', 'support', 'open-count'] });
    },
  });

  if (ticketQ.isLoading) {
    return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>;
  }
  if (!ticketQ.data) {
    return (
      <div>
        <Link to="/admin/tickets" className="text-sm text-brand-600 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Volver
        </Link>
        <p className="mt-4 text-slate-500">No se encontró el ticket.</p>
      </div>
    );
  }

  const ticket = ticketQ.data.ticket;
  const closed = ticket.status === 'closed';

  return (
    <div className="space-y-6">
      <Link to="/admin/tickets" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Volver a tickets
      </Link>

      <header className="flex items-start gap-3">
        <StatusPill status={ticket.status} />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">{ticket.subject}</h1>
          <p className="text-xs text-slate-500 mt-1">
            De <strong>{ticket.user.name || ticket.user.email}</strong> &lt;{ticket.user.email}&gt; · Abierto el{' '}
            {new Date(ticket.createdAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
        {!closed && (
          <button
            type="button"
            onClick={() => closeMut.mutate()}
            disabled={closeMut.isPending}
            className="btn-secondary text-xs"
          >
            Cerrar ticket
          </button>
        )}
      </header>

      <div className="space-y-3">
        {ticket.messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>

      {!closed ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reply.trim()) replyMut.mutate();
          }}
          className="rounded-xl border border-slate-200 bg-white p-4"
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder="Escribe la respuesta al usuario… (se enviará por email automáticamente)"
            className="input"
          />
          {replyMut.isError && <p className="mt-2 text-sm text-red-600">No hemos podido enviar.</p>}
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={!reply.trim() || replyMut.isPending}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              {replyMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Responder
            </button>
          </div>
        </form>
      ) : (
        <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 text-center">
          Este ticket está cerrado.
        </p>
      )}
    </div>
  );
}

/* ── Shared ──────────────────────────────────────────────── */

function MessageBubble({ message }: { message: TicketMessage }) {
  const isAdmin = message.authorRole === 'admin';
  return (
    <div className={cn('flex', isAdmin ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isAdmin ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200 text-slate-800',
        )}
      >
        <p className="whitespace-pre-line text-sm leading-relaxed">{message.body}</p>
        <p className={cn('text-[10px] mt-1.5', isAdmin ? 'text-brand-100' : 'text-slate-400')}>
          {isAdmin ? 'Tú (admin)' : 'Usuario'} ·{' '}
          {new Date(message.createdAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
        </p>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: 'open' | 'answered' | 'closed' }) {
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
        <Clock size={11} /> Abierto
      </span>
    );
  }
  if (status === 'answered') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-200 px-2 py-0.5 rounded-full">
        <CheckCircle2 size={11} /> Respondido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
      <XCircle size={11} /> Cerrado
    </span>
  );
}
